import { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Box,
  Autocomplete,
  FormControlLabel,
  Switch,
  Typography,
  Collapse,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { getDealValidationSchema } from "../../utils/validations";
import { DEAL_SOURCES, PRIORITY_LEVELS, DISPOSITION_OPTIONS, SUB_DISPOSITION_OPTIONS, REASON_FOR_CONTACT_OPTIONS, COMPLAINT_TYPE_OPTIONS, MACHINE_NUMBER_OPTIONS, ERROR_CODE_OPTIONS } from "../../utils/constants";
import { formatDateForInput } from "../../utils/helpers";
import axiosInstance from "../../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../../services/apiEndPoints";
const COMPLAINT_STATUS_OPTIONS = [
  "Credit note promised",
  "Refund request",
  "Cx Received juice",
  "Credit note processed",
  "Refund processed",
  "Resolved"
];

const COMMAND_CODE_OPTIONS = [
  // "500",
  // "1000"
  "TOKEN"
];

export const DealForm = ({
  initialValues,
  onSubmit,
  companies,
  contacts,
  stages,
  onAddCompany,
  onAddContact,
  isSubmitting,
  submitLabel = "Create Lead",
  showActions = true,
  onCancel,
  onClose,
}) => {
  const [showComplaintDetails, setShowComplaintDetails] = useState(false);

  const userId = localStorage.getItem("userId");

  const defaultValues = {
    dealName: "",
    company: "",
    contactName: "",
    amount: "",
    closingDate: "",
    stage: stages[0]?._id || "",
    priority: "medium",
    email: "",
    source: "",
    description: "",
    mobile: "",
    interest: "",
    subInterest: "",
    disposition: "",
    subDisposition: "",
    whatsappSent: false,
    remarks: "",
    followUpDate: "",
    // Complaint Fields
    complaintDate: "",
    complaintTime: "",
    loggedBy: "",
    callerName: "",
    paymentType: "",
    machineNumber: "",
    reasonForContact: "",
    complaintType: "",
    errorCode: "",
    juicesPushed: "",
    creditNoteJuices: "",
    commandCode: "",
    complaintStatus: "",
    refundAmount: "",
    customerEmailAddress: "",
    complaintComments: "",
    whatsappNumber: "",
    // UTM parameters
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_term: "",
    utm_content: "",
  };

  const transformedInitialValues = initialValues
    ? {
      dealName: initialValues.dealName || "",
      company:
        initialValues.companyName ||
        (typeof initialValues.company === "string"
          ? initialValues.company
          : initialValues.company?.name || ""),
      contactName:
        typeof initialValues.contactName === "string"
          ? initialValues.contactName
          : initialValues.contact
            ? `${initialValues.contact.firstName} ${initialValues.contact.lastName}`
            : "",
      amount: initialValues.amount || "",
      closingDate: initialValues.closingDate || "",
      stage:
        typeof initialValues.stage === "string"
          ? initialValues.stage
          : initialValues.stage?._id || "",
      priority: initialValues.priority || "medium",
      source: initialValues.source || "",
      description: initialValues.description || "",
      mobile: initialValues.mobile || "",
      email: initialValues.email || "",
      id: initialValues._id || "",
      interest: initialValues.interest || "",
      subInterest: initialValues.subInterest || "",
      disposition: initialValues.disposition || "",
      subDisposition: initialValues.subDisposition || "",
      whatsappSent: initialValues.whatsappSent || false,
      remarks: initialValues.remarks || "",
      followUpDate: initialValues.followUpDate || "",
      city: initialValues.city || "",
      // Complaint Fields
      complaintDate: initialValues.complaintDate || "",
      complaintTime: initialValues.complaintTime || "",
      loggedBy: initialValues.loggedBy || "",
      callerName: initialValues.callerName || "",
      paymentType: initialValues.paymentType || "",
      machineNumber: initialValues.machineNumber || "",
      reasonForContact: initialValues.reasonForContact || "",
      complaintType: initialValues.complaintType || "",
      errorCode: initialValues.errorCode || "",
      juicesPushed: initialValues.juicesPushed || "",
      creditNoteJuices: initialValues.creditNoteJuices || "",
      commandCode: initialValues.commandCode || "",
      complaintStatus: initialValues.complaintStatus || "",
      refundAmount: initialValues.refundAmount || "",
      customerEmailAddress: initialValues.customerEmailAddress || "",
      complaintComments: initialValues.complaintComments || "",
      whatsappNumber: initialValues.whatsappNumber || "",
      // UTM parameters
      utm_source: initialValues.utm_source || "",
      utm_medium: initialValues.utm_medium || "",
      utm_campaign: initialValues.utm_campaign || "",
      utm_term: initialValues.utm_term || "",
      utm_content: initialValues.utm_content || "",
    }
    : defaultValues;

  return (
    <Formik
      initialValues={transformedInitialValues}
      validationSchema={getDealValidationSchema(userId)}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting: formSubmitting,
        setFieldValue,
      }) => (
        <Form>
          {/* VALIDATION ONLY HERE */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="dealName"
                placeholder="Lead Name"
                value={values.dealName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dealName && Boolean(errors.dealName)}
                helperText={touched.dealName && errors.dealName}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="mobile"
                placeholder="Mobile No*"
                value={values.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.mobile && Boolean(errors.mobile)}
                helperText={touched.mobile && errors.mobile}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="company"
                placeholder="Company Name"
                value={values.company}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="closingDate"
                placeholder="Closing Date"
                type="date"
                value={
                  values.closingDate
                    ? formatDateForInput(values.closingDate)
                    : ""
                }
                onChange={handleChange}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>

          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="email"
                placeholder="Email Address"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={values.priority}
                  onChange={handleChange}
                  label="Priority"
                  sx={{
                    borderRadius: "12px",
                    height: "44px",
                  }}
                >
                  {PRIORITY_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>



          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                name="source"
                value={
                  DEAL_SOURCES.find((source) => source === values.source) || ""
                }
                onChange={(event, newValue) => {
                  setFieldValue("source", newValue || "");
                }}
                options={DEAL_SOURCES}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Deal Source"
                    sx={{
                      borderRadius: "12px",
                      "& .MuiInputBase-root": {
                        height: "44px",
                        borderRadius: "12px",
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="city"
                placeholder="Location/City"
                value={values.city || ""}
                onChange={handleChange}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            name="description"
            placeholder="Description"
            // multiline
            // rows={3}
            value={values.description}
            onChange={handleChange}
            sx={{
              mb: 2,
              borderRadius: "12px",
              "& .MuiInputBase-root": {
                borderRadius: "12px",
              },
            }}
          />

          {userId === "69afdc065323c68fd76cdabf" && (
            <>
              <Box
                onClick={() => setShowComplaintDetails(!showComplaintDetails)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  p: '12px 16px',
                  mt: 2.5,
                  mb: 2,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: showComplaintDetails ? '#6366f1' : '#e2e8f0',
                  bgcolor: showComplaintDetails ? '#f8fafc' : '#ffffff',
                  transition: 'all 0.2s',
                  userSelect: 'none',
                  '&:hover': {
                    borderColor: '#6366f1',
                    bgcolor: '#f8fafc',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '1.2rem', lineHeight: 1 }}>🚨</Typography>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: showComplaintDetails ? '#6366f1' : '#0f172a', lineHeight: 1.2 }}>
                      Complaint & Ticket Details
                    </Typography>
                    {/* <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.2 }}>
                      Optional details for customer complaints or machine errors
                    </Typography> */}
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                  {showComplaintDetails ? 'Hide Section ✕' : 'Show Section ＋'}
                </Typography>
              </Box>

              <Collapse in={showComplaintDetails} sx={{ width: '100%' }}>
                <Box sx={{ pt: 1 }}>


                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={MACHINE_NUMBER_OPTIONS}
                        value={values.machineNumber || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("machineNumber", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Machine ID *"
                            error={touched.machineNumber && Boolean(errors.machineNumber)}
                            helperText={touched.machineNumber && errors.machineNumber}
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Payment Type</InputLabel>
                        <Select
                          name="paymentType"
                          value={values.paymentType}
                          onChange={handleChange}
                          label="Payment Type"
                          sx={{
                            borderRadius: "12px",
                            height: "44px",
                          }}
                        >
                          <MenuItem value="">Select Payment Type</MenuItem>
                          <MenuItem value="Cash">Cash</MenuItem>
                          <MenuItem value="Debit Card">Debit Card</MenuItem>
                          <MenuItem value="Credit Card">Credit Card</MenuItem>
                          <MenuItem value="Wallet">Wallet</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={REASON_FOR_CONTACT_OPTIONS}
                        value={values.reasonForContact || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("reasonForContact", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Reason For Contact"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={COMPLAINT_TYPE_OPTIONS}
                        value={values.complaintType || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("complaintType", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Complaint Type"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={ERROR_CODE_OPTIONS}
                        value={values.errorCode || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("errorCode", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Error Code"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="refundAmount"
                        placeholder="Refunds (AED)"
                        value={values.refundAmount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: "12px",
                          "& .MuiInputBase-root": {
                            height: "44px",
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="juicesPushed"
                        placeholder="Juices Pushed Count"
                        value={values.juicesPushed}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: "12px",
                          "& .MuiInputBase-root": {
                            height: "44px",
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="creditNoteJuices"
                        placeholder="Credit Note Juices Count"
                        value={values.creditNoteJuices}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: "12px",
                          "& .MuiInputBase-root": {
                            height: "44px",
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        fullWidth
                        options={COMMAND_CODE_OPTIONS}
                        value={values.commandCode || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("commandCode", newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Command Code"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="customerEmailAddress"
                        placeholder="Customer Email Address"
                        value={values.customerEmailAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{
                          borderRadius: "12px",
                          "& .MuiInputBase-root": {
                            height: "44px",
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={12}>
                      <Autocomplete
                        fullWidth
                        options={COMPLAINT_STATUS_OPTIONS}
                        value={values.complaintStatus || ""}
                        onChange={(event, newValue) => {
                          setFieldValue("complaintStatus", newValue || "");
                          if (stages && stages.length > 0) {
                            const matchedStage = stages.find(
                              (s) => s.name.toLowerCase().trim() === (newValue || "").toLowerCase().trim()
                            );
                            if (matchedStage) {
                              setFieldValue("stage", matchedStage._id);
                            } else {
                              setFieldValue("stage", stages[0]._id);
                            }
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Status *"
                            error={touched.complaintStatus && Boolean(errors.complaintStatus)}
                            helperText={touched.complaintStatus && errors.complaintStatus}
                            sx={{
                              borderRadius: "12px",
                              "& .MuiInputBase-root": {
                                height: "44px",
                                borderRadius: "12px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>


                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      name="complaintComments"
                      placeholder="Complaint Comments"
                      multiline
                      rows={3}
                      value={values.complaintComments}
                      onChange={handleChange}
                      sx={{
                        // mb: 2.5,
                        borderRadius: "12px",
                        "& .MuiInputBase-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </Grid>

                </Box>
              </Collapse>
            </>
          )}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="whatsappNumber"
                placeholder="WhatsApp Number"
                value={values.whatsappNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1, color: '#0f172a' }}>
            Marketing UTM Parameters
          </Typography>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="utm_source"
                placeholder="UTM Source (e.g. google_ads)"
                value={values.utm_source}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="utm_medium"
                placeholder="UTM Medium (e.g. cpc)"
                value={values.utm_medium}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                name="utm_campaign"
                placeholder="UTM Campaign"
                value={values.utm_campaign}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                name="utm_term"
                placeholder="UTM Term"
                value={values.utm_term}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                name="utm_content"
                placeholder="UTM Content"
                value={values.utm_content}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: "44px",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
          </Grid>

          {showActions && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                pt: 2,
                borderTop: onCancel ? "1px solid #E5E7EB" : "none",
              }}
            >
              {onCancel && (
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={isSubmitting || formSubmitting}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "capitalize",
                  }}
                  onClick={() => {
                    onCancel?.();
                    onClose();
                  }}
                >
                  Cancel
                </Button>
              )}

              <Button
                variant="contained"
                fullWidth
                type="submit"
                disabled={isSubmitting || formSubmitting}
                sx={{
                  bgcolor: "#6366f1",
                  borderRadius: "12px",
                  textTransform: "capitalize",
                  mb: 1,
                }}
              >
                {isSubmitting || formSubmitting ? "Saving..." : submitLabel}
              </Button>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
};
